// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useState, useMemo } from 'react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from '@apollo/client/react';
import { Card, CardContent, CardHeader, CardTitle, Button, Input } from '@burdenoff/fe-libs/ui';
import { Save, ArrowLeft, Loader2 } from 'lucide-react';
import { usePlanMagnet, usePlanMagnetNavigate } from '../providers/PlanMagnetProvider';
import { useParams } from 'react-router-dom';

const GET_RELEASE = gql`
  query GetReleaseForEdit($id: ID!) {
    planmagnetRelease(id: $id) {
      id
      name
      description
      version
      status
      plannedDate
      releasedAt
      releaseNotes
      ownerId
      tags
    }
  }
`;

const UPDATE_RELEASE = gql`
  mutation UpdateRelease($id: ID!, $input: UpdateReleaseInput!) {
    planmagnetUpdateRelease(id: $id, input: $input) {
      id
      name
      description
      version
      status
      plannedDate
      releaseNotes
      tags
      updatedAt
    }
  }
`;

export function ProductsEditReleasePagePage() {
  const { workspaceId } = usePlanMagnet();
  const navigate = usePlanMagnetNavigate();
  const params = useParams<{ id: string }>();
  const releaseId = params.id ?? '';

  const {
    data,
    loading,
    error,
  } = // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    useQuery<Record<string, any>>(GET_RELEASE, {
      variables: { id: releaseId },
      skip: !releaseId,
    });

  const [updateRelease, { loading: saving, error: saveError }] = useMutation(UPDATE_RELEASE, {
    refetchQueries: ['ListReleases', 'GetReleaseForEdit'],
  });

  const release = data?.planmagnetRelease;

  const defaultForm = useMemo(
    () => ({
      version: release?.version ?? '',
      name: release?.name ?? '',
      description: release?.description ?? '',
      status: release?.status ?? 'DRAFT',
      plannedDate: release?.plannedDate
        ? new Date(release.plannedDate).toISOString().split('T')[0]
        : '',
      releaseNotes: release?.releaseNotes ?? '',
      tags: release?.tags?.join(', ') ?? '',
    }),
    [release]
  );

  const [formOverrides, setFormOverrides] = useState<Record<string, string>>({});
  const form = { ...defaultForm, ...formOverrides };
  const setForm = (val: Record<string, string>) => setFormOverrides(val);

  const statuses = [
    'DRAFT',
    'PLANNING',
    'IN_PROGRESS',
    'READY',
    'RELEASED',
    'CANCELLED',
    'ROLLED_BACK',
  ] as const;

  const handleSave = () => {
    planmagnetUpdateRelease({
      variables: {
        id: releaseId,
        input: {
          version: form.version || undefined,
          name: form.name || undefined,
          description: form.description || undefined,
          status: form.status || undefined,
          plannedDate: form.plannedDate || undefined,
          releaseNotes: form.releaseNotes || undefined,
          tags: form.tags
            ? form.tags
                .split(',')
                .map((t: string) => t.trim())
                .filter(Boolean)
            : undefined,
        },
      },
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-destructive">Failed to load release: {error.message}</div>;
  }

  if (!release) return <div className="p-6 text-muted-foreground">Release not found.</div>;

  return (
    <div className="space-y-6 p-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/products/releases')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold">Edit Release</h1>
            <p className="text-sm text-muted-foreground">
              {release.version} - {release.name}
            </p>
          </div>
        </div>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-1 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>

      {saveError && (
        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          Failed to save: {saveError.message}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Release Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Version</label>
              <Input
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                className="mt-1"
                placeholder="v1.0.0"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Description</label>
            <textarea
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[80px]"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Status</label>
              <select
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {statuses.map((s) => (
                  <option key={s} value={s} className="capitalize">
                    {s.toLowerCase().replace('_', ' ')}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Planned Date</label>
              <Input
                type="date"
                value={form.plannedDate}
                onChange={(e) => setForm({ ...form, plannedDate: e.target.value })}
                className="mt-1"
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Tags (comma-separated)</label>
            <Input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="mt-1"
              placeholder="e.g. critical, security, performance"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Release Notes</label>
            <textarea
              className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[120px] font-mono"
              value={form.releaseNotes}
              onChange={(e) => setForm({ ...form, releaseNotes: e.target.value })}
              placeholder="Describe what changed in this release..."
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
